import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Mail, CheckCircle, ArrowLeft, RefreshCw } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'

const EmailVerificationPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  
  const { resendConfirmationEmail, error } = useAuthStore()
  const navigate = useNavigate()
  
  const email = searchParams.get('email') || ''

  const handleResendEmail = async () => {
    if (!email) return
    
    setIsResending(true)
    setResendSuccess(false)
    
    try {
      await resendConfirmationEmail(email)
      setResendSuccess(true)
    } catch (error) {
      // Error is handled by the store
    } finally {
      setIsResending(false)
    }
  }

  const handleBackToLogin = () => {
    navigate('/auth')
  }

  return (
    <>
      <Helmet>
        <title>Verify Your Email - AI Decision Maker</title>
        <meta name="description" content="Please verify your email address to complete your registration" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Back Button */}
          <div className="text-left">
            <button
              onClick={handleBackToLogin}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Sign In
            </button>
          </div>

          {/* Header */}
          <div className="text-center">
            <div className="mx-auto h-12 w-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg flex items-center justify-center">
              <Mail className="h-6 w-6 text-white" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Check your email
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              We've sent a verification link to
            </p>
            <p className="text-sm font-medium text-gray-900">
              {email}
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-gray-900">
                    Verify your email address
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Click the verification link in the email to activate your account and start using AI Decision Maker.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Mail className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Didn't receive the email?
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        Check your spam folder or try resending the verification email.
                      </p>
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={handleResendEmail}
                        disabled={isResending}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isResending ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Resend verification email'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {resendSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-800">
                        Verification email sent!
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        Please check your inbox and click the verification link.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">
                        Error sending email
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        {error}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already verified?{' '}
              <button
                onClick={handleBackToLogin}
                className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
              >
                Sign in to your account
              </button>
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

export default EmailVerificationPage 